/**
 * Admin-only service: global stats, user management, agent hierarchy.
 * All functions require role = 'admin' (enforced via resolveRoleContext).
 */

import { supabase } from '../lib/supabase';
import {
  handleQuery,
  accessDeniedResponse,
  resolveRoleContext,
  formatSupabaseError
} from './utils';
import * as eventsService from './eventsService';
import * as leadsService from './leadsService';
import * as registrationsService from './registrationsService';
import * as paymentsService from './paymentsService';
import * as dashboardService from './dashboardService';

const USERS_TABLE = 'users';
// Table name: agent_hierarchies or agenthierarchies (check your schema)
const HIERARCHY_TABLE = 'agent_hierarchies';

/**
 * Get global dashboard stats for admin. Uses get_dashboard_stats RPC when available,
 * otherwise aggregates from events, leads, event_registrations, payments.
 * @param {{ role?: string, userId?: string }} [options]
 */
export const getAdminStats = async (options = {}) => {
  const roleContext = await resolveRoleContext(options);
  if (roleContext.error) return { data: null, error: roleContext.error };
  if (roleContext.role !== 'admin') {
    return accessDeniedResponse('Only admins can access global dashboard stats.');
  }

  const { data: rpcStats, error: rpcError } = await dashboardService.getDashboardStats({
    role: 'admin',
    userId: roleContext.userId
  });

  if (!rpcError && rpcStats && typeof rpcStats === 'object') {
    return { data: rpcStats, error: null };
  }

  try {
    const [eventsRes, leadsRes, regsRes, paymentsRes] = await Promise.all([
      eventsService.listEvents({ role: 'admin', userId: roleContext.userId }),
      leadsService.listLeads({ role: 'admin', userId: roleContext.userId }),
      registrationsService.listRegistrations({ role: 'admin', userId: roleContext.userId }),
      paymentsService.listPayments({ role: 'admin', userId: roleContext.userId })
    ]);

    const events = eventsRes.data ?? [];
    const leads = leadsRes.data ?? [];
    const registrations = regsRes.data ?? [];
    const payments = paymentsRes.data ?? [];

    const totalEvents = Array.isArray(events) ? events.length : 0;
    const totalLeads = Array.isArray(leads) ? leads.length : 0;
    const totalRegistrations = Array.isArray(registrations) ? registrations.length : 0;
    const totalPayments = Array.isArray(payments) ? payments.length : 0;
    const convertedLeads = Array.isArray(leads) ? leads.filter(l => (l.status || '').toLowerCase() === 'converted').length : 0;
    const conversionRate = totalLeads > 0 ? Math.round((convertedLeads / totalLeads) * 1000) / 10 : 0;
    const totalRevenue = Array.isArray(payments)
      ? payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0)
      : 0;

    return {
      data: {
        total_events: totalEvents,
        total_leads: totalLeads,
        total_registrations: totalRegistrations,
        total_payments: totalPayments,
        converted_leads: convertedLeads,
        conversion_rate: conversionRate,
        total_revenue: totalRevenue
      },
      error: null
    };
  } catch (err) {
    console.error('[AdminService] getAdminStats error:', err);
    return { data: null, error: formatSupabaseError(err, 'Unable to load admin stats.') };
  }
};

/**
 * List all users (admin only). RLS should allow SELECT for admin.
 */
export const listUsers = async (options = {}) => {
  const roleContext = await resolveRoleContext(options);
  if (roleContext.error) return { data: null, error: roleContext.error };
  if (roleContext.role !== 'admin') {
    return accessDeniedResponse('Only admins can list users.');
  }

  const query = supabase
    .from(USERS_TABLE)
    .select('*')
    .order('created_at', { ascending: false });

  return handleQuery(query, { fallbackMessage: 'Unable to load users.' });
};

/**
 * Update a user's role and/or status (admin only).
 * @param {string} targetUserId
 * @param {{ role?: string, status?: string }} updates
 * @param {{ role?: string, userId?: string }} [options]
 */
export const updateUserRoleStatus = async (targetUserId, updates, options = {}) => {
  const roleContext = await resolveRoleContext(options);
  if (roleContext.error) return { data: null, error: roleContext.error };
  if (roleContext.role !== 'admin') {
    return accessDeniedResponse('Only admins can update user role or status.');
  }

  const payload = {};
  if (updates.role != null) payload.role = updates.role;
  if (updates.status != null) payload.status = updates.status;
  if (Object.keys(payload).length === 0) {
    return { data: null, error: { message: 'No role or status to update.', code: 'invalid_input' } };
  }

  const query = supabase
    .from(USERS_TABLE)
    .update(payload)
    .eq('id', targetUserId)
    .select()
    .single();

  return handleQuery(query, {
    fallbackMessage: 'Unable to update user.',
    allowEmpty: false
  });
};

/**
 * List agent hierarchy rows (admin or agent viewing own tree).
 * Table: agent_hierarchies (parent_agent_id, child_user_id, relationship_type).
 */
export const listAgentHierarchy = async (options = {}) => {
  const roleContext = await resolveRoleContext(options);
  if (roleContext.error) return { data: null, error: roleContext.error };
  if (roleContext.role !== 'admin' && roleContext.role !== 'agent') {
    return accessDeniedResponse('Only admins and agents can view hierarchy.');
  }

  let query = supabase.from(HIERARCHY_TABLE).select('*').order('created_at', { ascending: false });

  if (roleContext.role === 'agent') {
    query = query.or(`parent_agent_id.eq.${roleContext.userId},child_user_id.eq.${roleContext.userId}`);
  }

  return handleQuery(query, { fallbackMessage: 'Unable to load agent hierarchy.' });
};

/**
 * Add a sub-agent row (admin only).
 * @param {string} parentAgentId
 * @param {string} childUserId
 * @param {string} [relationshipType]
 */
export const addSubAgent = async (parentAgentId, childUserId, relationshipType = 'sub_agent', options = {}) => {
  const roleContext = await resolveRoleContext(options);
  if (roleContext.error) return { data: null, error: roleContext.error };
  if (roleContext.role !== 'admin') {
    return accessDeniedResponse('Only admins can add agent hierarchy rows.');
  }

  const payload = {
    parent_agent_id: parentAgentId,
    child_user_id: childUserId,
    relationship_type: relationshipType || 'sub_agent'
  };

  const query = supabase.from(HIERARCHY_TABLE).insert(payload).select().single();
  return handleQuery(query, {
    fallbackMessage: 'Unable to add sub-agent.',
    allowEmpty: false
  });
};

/**
 * Get hierarchy tree structure (admin sees all; agent sees own subtree).
 * Returns flat list with parent/child info for building tree in UI.
 */
export const getHierarchyTree = async (options = {}) => {
  const { data, error } = await listAgentHierarchy(options);
  if (error) return { data: null, error };
  const rows = data ?? [];
  return { data: rows, error: null };
};
